import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { FileText, Image as ImageIcon, Eye, Download, Check, Unlink, Loader2, Paperclip, AlertTriangle, Layers } from 'lucide-react';

const fmt = (v) => (v != null ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(v) : '—');
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('fr-FR') : '—');

const FIN = {
    comptabilise: { label: 'Comptabilisé', cls: 'bg-emerald-50 text-emerald-700' },
    valide: { label: 'Validé', cls: 'bg-blue-50 text-blue-700' },
    doublon: { label: 'Doublon à vérifier', cls: 'bg-amber-50 text-amber-700' },
    justificatif_seul: { label: 'Justificatif seul — non comptabilisé', cls: 'bg-slate-100 text-slate-600' },
    lie_ecart: { label: 'Lié à une dépense d\'un autre montant', cls: 'bg-amber-50 text-amber-800' },
    non_valide: { label: 'Non validé', cls: 'bg-gray-100 text-gray-500' },
};

const CAT = (c) => (c ? c.replace(/_/g, ' ').replace(/^./, (x) => x.toUpperCase()) : 'Dépense');

function DocViewer({ doc, canManage, onClose }) {
    const [url, setUrl] = useState(null);
    useEffect(() => {
        if (!doc) return undefined;
        let u;
        axios.get(`${API_URL}/api/vehicle-documents/${doc.id}/file`, { withCredentials: true, responseType: 'blob' })
            .then((r) => { u = URL.createObjectURL(r.data); setUrl(u); })
            .catch(() => toast.error('Document indisponible'));
        return () => { if (u) URL.revokeObjectURL(u); setUrl(null); };
    }, [doc]);

    const download = async () => {
        const r = await axios.get(`${API_URL}/api/vehicle-documents/${doc.id}/file?download=1`, { withCredentials: true, responseType: 'blob' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(r.data);
        a.download = doc.filename || 'justificatif';
        a.click();
    };

    const isPdf = (doc?.content_type || '').includes('pdf');
    return (
        <Dialog open={!!doc} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-3xl w-[96vw] p-0 gap-0 overflow-hidden bg-white" data-testid="vdoc-viewer">
                <DialogHeader className="px-5 pt-5 pb-3 border-b border-gray-100">
                    <DialogTitle className="text-base flex items-center justify-between gap-3 pr-6">
                        <span className="truncate">{doc?.fournisseur || doc?.filename}</span>
                        {canManage && (
                            <Button size="sm" variant="outline" onClick={download} className="h-8 text-xs shrink-0" data-testid="vdoc-download-btn">
                                <Download className="w-3.5 h-3.5 mr-1.5" />Télécharger
                            </Button>
                        )}
                    </DialogTitle>
                </DialogHeader>
                <div className="bg-gray-50 h-[70vh] flex items-center justify-center" onContextMenu={(e) => !canManage && e.preventDefault()}>
                    {!url ? <Loader2 className="w-6 h-6 animate-spin text-[#6366F1]" />
                        : isPdf ? <iframe src={`${url}#toolbar=${canManage ? 1 : 0}`} title="Justificatif" className="w-full h-full" data-testid="vdoc-pdf" />
                            : <img src={url} alt="Justificatif" className="max-w-full max-h-full object-contain" draggable={false} data-testid="vdoc-image" />}
                </div>
            </DialogContent>
        </Dialog>
    );
}

function DocRow({ d, canManage, onOpen, onConfirm, onDetach }) {
    const fin = FIN[d.statut_financier] || FIN.non_valide;
    const Icon = (d.content_type || '').includes('pdf') ? FileText : ImageIcon;
    const pending = d.statut_lien === 'a_confirmer';
    return (
        <div className={`flex items-start gap-3 p-3 rounded-xl border ${pending ? 'border-amber-200 bg-amber-50/40' : 'border-gray-100 bg-white'} transition-colors hover:border-[#6366F1]/30`} data-testid={`vdoc-row-${d.id}`}>
            <button onClick={() => onOpen(d)} className="shrink-0 w-10 h-10 rounded-lg bg-[#6366F1]/10 text-[#6366F1] flex items-center justify-center" data-testid={`vdoc-open-${d.id}`}>
                <Icon className="w-5 h-5" />
            </button>
            <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-medium text-gray-900 truncate">{d.fournisseur || d.filename}</p>
                    <span className="text-sm font-mono font-semibold text-gray-900 shrink-0">{fmt(d.montant_ttc)}</span>
                </div>
                <p className="text-xs text-gray-500 mt-0.5 truncate">
                    {fmtDate(d.date_facture)} · {CAT(d.categorie)}{d.numero_facture ? ` · N° ${d.numero_facture}` : ''}
                    {d.montant_tva != null ? ` · TVA ${fmt(d.montant_tva)}` : ''}
                </p>
                <div className="flex flex-wrap items-center gap-1.5 mt-2">
                    <span className={`text-[11px] px-2 py-0.5 rounded-full ${fin.cls}`} data-testid={`vdoc-fin-${d.id}`}>{fin.label}</span>
                    {pending && <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />À confirmer{!d.plaque_fiable ? ' · plaque douteuse' : ''}</span>}
                    {d.statut_financier === 'lie_ecart' && d.frais_lie && <span className="text-[11px] text-amber-800" data-testid={`vdoc-ecart-${d.id}`}>Lié à « {d.frais_lie.nom.trim()} » ({fmt(d.frais_lie.montant_ttc)}) — écart {fmt((d.montant_ttc || 0) - (d.frais_lie.montant_ttc || 0))} non comptabilisé</span>}
                    {d.multi_vehicules && <span className="text-[11px] px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-600 flex items-center gap-1"><Layers className="w-3 h-3" />Multi-véhicules</span>}
                </div>
                <div className="flex items-center gap-1 mt-2">
                    <Button size="sm" variant="ghost" onClick={() => onOpen(d)} className="h-8 px-2 text-xs text-[#6366F1]" data-testid={`vdoc-view-${d.id}`}><Eye className="w-3.5 h-3.5 mr-1" />Voir</Button>
                    {canManage && pending && <Button size="sm" variant="ghost" onClick={() => onConfirm(d)} className="h-8 px-2 text-xs text-emerald-600" data-testid={`vdoc-confirm-${d.id}`}><Check className="w-3.5 h-3.5 mr-1" />Confirmer</Button>}
                    {canManage && <Button size="sm" variant="ghost" onClick={() => onDetach(d)} className="h-8 px-2 text-xs text-gray-500" data-testid={`vdoc-detach-${d.id}`}><Unlink className="w-3.5 h-3.5 mr-1" />{pending ? 'Rejeter' : 'Détacher'}</Button>}
                </div>
            </div>
        </div>
    );
}

export const VehicleDocuments = ({ vehicleId }) => {
    const [data, setData] = useState(null);
    const [viewing, setViewing] = useState(null);

    const load = useCallback(() => {
        axios.get(`${API_URL}/api/vehicles/${vehicleId}/documents`, { withCredentials: true })
            .then((r) => setData(r.data)).catch(() => setData({ documents: [], count: 0, can_manage: false }));
    }, [vehicleId]);
    useEffect(() => { load(); }, [load]);

    const act = async (fn, msg) => {
        try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    };
    const onConfirm = (d) => act(() => axios.post(`${API_URL}/api/vehicle-documents/${d.id}/confirm`, {}, { withCredentials: true }), 'Justificatif confirmé');
    const onDetach = (d) => {
        if (!window.confirm('Retirer ce justificatif du dossier ? Aucun frais ni montant ne sera modifié.')) return;
        act(() => axios.delete(`${API_URL}/api/vehicle-documents/${d.id}`, { withCredentials: true }), 'Justificatif retiré du dossier');
    };

    if (!data) return null;
    const docs = data.documents || [];
    return (
        <div className="glass card-base p-5 sm:p-6 border border-gray-200 mt-6" data-testid="vehicle-documents-section">
            <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-[#6366F1]/10 flex items-center justify-center"><Paperclip className="w-5 h-5 text-[#6366F1]" /></div>
                <div className="flex-1">
                    <h2 className="font-semibold text-lg">Factures et justificatifs</h2>
                    <p className="text-xs text-gray-400">Documents scannés rattachés à ce dossier</p>
                </div>
                <span className="text-sm font-semibold px-2.5 py-1 rounded-full bg-gray-100 text-gray-700" data-testid="vehicle-documents-count">{data.count}</span>
            </div>
            {docs.length === 0
                ? <p className="text-sm text-gray-400 py-6 text-center" data-testid="vehicle-documents-empty">Aucun justificatif rattaché pour le moment.</p>
                : <div className="space-y-2">{docs.map((d) => <DocRow key={d.id} d={d} canManage={data.can_manage} onOpen={setViewing} onConfirm={onConfirm} onDetach={onDetach} />)}</div>}
            <DocViewer doc={viewing} canManage={data.can_manage} onClose={() => setViewing(null)} />
        </div>
    );
};
